/**
 * useUserSearch Hook
 * Shared between web and mobile for searching users by display name
 */

import { useCallback, useRef, useState } from 'react';
import { ApiError, type HttpClient } from '../api/httpClient';
import type { SearchResult } from '../socialTypes';

// =============================================================================
// Constants
// =============================================================================

const PAGE_SIZE = 20;

// =============================================================================
// Types
// =============================================================================

export type { SearchResult } from '../socialTypes';

interface SearchResponse {
  results: SearchResult[];
  total: number;
  hasMore: boolean;
}

export interface UseUserSearchOptions {
  httpClient: HttpClient;
}

export interface UseUserSearchResult {
  results: SearchResult[];
  total: number;
  hasMore: boolean;
  loading: boolean;
  error: string | null;
  search: (query: string) => Promise<void>;
  loadMore: () => Promise<void>;
  clear: () => void;
}

// =============================================================================
// Hook
// =============================================================================

export function useUserSearch(options: UseUserSearchOptions): UseUserSearchResult {
  const { httpClient } = options;
  const [results, setResults] = useState<SearchResult[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastQueryRef = useRef('');
  const accumulatedRef = useRef<SearchResult[]>([]);

  const search = useCallback(
    async (query: string) => {
      if (!query || query.trim().length < 2) {
        setResults([]);
        setTotal(0);
        setHasMore(false);
        accumulatedRef.current = [];
        lastQueryRef.current = '';
        return;
      }

      lastQueryRef.current = query;
      accumulatedRef.current = [];
      setLoading(true);
      setError(null);

      try {
        const data = await httpClient.get<SearchResponse>('/api/users/search', {
          q: query,
          limit: String(PAGE_SIZE),
          offset: '0',
        });
        accumulatedRef.current = data.results || [];
        setResults(accumulatedRef.current);
        setTotal(data.total ?? 0);
        setHasMore(data.hasMore ?? false);
      } catch (err) {
        const msg = ApiError.getUserMessage(err, 'Failed to search users');
        setError(msg);
        setResults([]);
        accumulatedRef.current = [];
      } finally {
        setLoading(false);
      }
    },
    [httpClient],
  );

  const loadMore = useCallback(async () => {
    if (!lastQueryRef.current || !hasMore || loading) return;

    setLoading(true);
    setError(null);

    try {
      const data = await httpClient.get<SearchResponse>('/api/users/search', {
        q: lastQueryRef.current,
        limit: String(PAGE_SIZE),
        offset: String(accumulatedRef.current.length),
      });
      accumulatedRef.current = [...accumulatedRef.current, ...(data.results || [])];
      setResults(accumulatedRef.current);
      setHasMore(data.hasMore ?? false);
    } catch (err) {
      const msg = ApiError.getUserMessage(err, 'Failed to load more results');
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [httpClient, hasMore, loading]);

  const clear = useCallback(() => {
    setResults([]);
    setTotal(0);
    setHasMore(false);
    setError(null);
    accumulatedRef.current = [];
    lastQueryRef.current = '';
  }, []);

  return {
    results,
    total,
    hasMore,
    loading,
    error,
    search,
    loadMore,
    clear,
  };
}
